import { isEqual } from 'lodash-es';
import { Instance, types } from 'mobx-state-tree';
import ApiActionFactory from '../../api/api-action-factory';
import {
    NEWS_AND_MEDIA_INFO_ENDPOINT,
    NEWS_AND_MEDIA_ENDPOINT,
    NEWS_AND_MEDIA_DETAILS_ENDPOINT,
    NEWS_AND_MEDIA_SUBMIT_COMMENT_ENDPOINT,
    NEWS_AND_MEDIA_CONTRIBUTORS_ENDPOINT,
} from '@/api/endpoints';
import { isDev } from '@utils/build-utils';
import { snakeCase, valueSort } from '@utils/utils';
import { TypeCategoryDataPropeties } from '@stores/content-stores/models/blog-category-model';
import { COMMENT_MESSAGE_RESET, SEGMENT_TAGS } from '@/constants';

import ApplicationStoreInstance from '@stores/application-store';
import NewsAndMediaInfoModel from '@stores/content-stores/models/news-and-media-info-model';
import NewsAndMediaModel, {
    TypeMediaModel,
    MediaDetailsModel,
} from '@stores/content-stores/models/news-and-media-model';
import StoreFactory from '../factories/store';

const NewsAndMediaStore = StoreFactory({
    modelName: 'NewsAndMediaStore',
    models: [
        ApiActionFactory({
            retrieveNewsAndMediaInfo: {
                endpoint: NEWS_AND_MEDIA_INFO_ENDPOINT,
                target: 'newsAndMediaInfo',
            },
            retrieveNewsAndMedia: {
                endpoint: NEWS_AND_MEDIA_ENDPOINT,
                target: 'newsAndMedia',
            },
            retrieveMediaDetails: {
                endpoint: NEWS_AND_MEDIA_DETAILS_ENDPOINT,
                target: 'mediaDetails',
                onTrigger(self: any) {
                    self.resetMediaDetails();
                },
            },
            retrieveBlogsContributors: {
                endpoint: NEWS_AND_MEDIA_CONTRIBUTORS_ENDPOINT,
                target: 'blogsContributors',
            },
            submitComment: {
                endpoint: NEWS_AND_MEDIA_SUBMIT_COMMENT_ENDPOINT,
                target: 'submittedCommentResponse',
                onLoad(self: any, config: any) {
                    setTimeout(() => {
                        self.resetSubmittedCommentResponse();
                    }, COMMENT_MESSAGE_RESET);
                },
            },
        }),
    ],
    modelStructure: {
        isLoading: false,
        newsAndMediaInfo: types.maybeNull(NewsAndMediaInfoModel),
        newsAndMedia: types.maybeNull(NewsAndMediaModel),
        blogsContributors: types.frozen({}),
        mediaDetails: types.maybeNull(MediaDetailsModel),
        submittedCommentResponse: types.frozen({}),
        keyedMedias: types.frozen({}),
    },
})
    .actions((self) => ({
        resetSubmittedCommentResponse() {
            self.submittedCommentResponse = {};
        },
        resetMediaDetails() {
            self.mediaDetails = {};
        },
        fetchBlogsContributors() {
            if (isEqual(self.blogsContributors, {})) {
                self.retrieveBlogsContributors();
            }
            return self.blogsContributors;
        },
        fetchNewsAndMediaInfo() {
            if (!self.newsAndMediaInfo?.fields || self.newsAndMediaInfo?.fields?.length === 0) {
                self.retrieveNewsAndMediaInfo();
            }
        },
        fetchNewsAndMedia() {
            if (!self.newsAndMedia?.medias || self.newsAndMedia?.medias?.length === 0) {
                self.retrieveNewsAndMedia();
            }
        },
    }))
    .views((self) => ({
        get featuredMedias(): TypeMediaModel[] {
            const mediasMap = self.newsAndMedia?.medias;
            if (!mediasMap) return [];

            return Array.from(mediasMap.values() as Iterable<TypeMediaModel>).filter(
                (m) => m.isFeatured
            );
        },
        get mediasArray() {
            return Array.from(self.newsAndMedia?.medias.values() ?? []).filter(
                (m) => !m.tags.includes('upcoming')
            );
        },
        get upcomingMediasArray() {
            return Array.from(self.newsAndMedia?.medias.values() ?? []).filter((m) =>
                m.tags.includes('upcoming')
            );
        },
        get mediasObject() {
            return Object.fromEntries(self.newsAndMedia?.medias ?? []);
        },
    }))
    .views((self) => ({
        getNewsAndMediaInfo() {
            const userLocale = snakeCase(
                ApplicationStoreInstance.userLocale
            ) as keyof TypeCategoryDataPropeties;
            const localizedTypes: Record<string, string> = {};
            const localizedTags: Record<string, string> = {};
            const localizedSegments: Record<string, string> = {};

            self.newsAndMediaInfo?.tags.forEach((tag: string, tagId: string) => {
                // @ts-ignore
                if (SEGMENT_TAGS.includes(tagId)) {
                    localizedSegments[tagId] = tag[userLocale];
                }
                // @ts-ignore
                else {
                    localizedTags[tagId] = tag[userLocale];
                }
            });

            self.newsAndMedia?.medias?.forEach((m) => {
                localizedTypes[m.mediaTypes] = '';
            });

            return {
                segments: valueSort(localizedSegments),
                tags: valueSort(localizedTags),
                types: valueSort(localizedTypes),
            };
        },
        getNewsAndMedia() {
            return {};
        },
    }));

export type TypeNewsAndMediaStore = Instance<typeof NewsAndMediaStore>;

const NewsAndMediaStoreInstance = NewsAndMediaStore.create();

declare const window: Window &
    typeof globalThis & {
        NewsAndMediaStore: typeof NewsAndMediaStoreInstance;
    };

if (window && isDev()) {
    window.NewsAndMediaStore = NewsAndMediaStoreInstance;
}

export default NewsAndMediaStore;
